import { useState } from 'react';
import { inventoryService } from '../services/inventoryService';

const InventoryValueReportPage = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('summary'); // 'summary', 'category', 'detail'

  const setDateRange = async (start, end) => {
    setStartDate(start);
    setEndDate(end);
    
    // Automatically fetch report with the new dates
    setLoading(true);
    setError(null);
    try {
      const data = await inventoryService.getInventoryValueAsOfDate(start || null, end);
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate report');
      console.error('Failed to fetch inventory value report:', err);
    } finally {
      setLoading(false);
    }
  };

  const quickDateRanges = {
    periods: [
      {
        label: 'Last Year (2025)',
        start: '2025-01-01',
        end: '2025-12-31'
      },
      {
        label: 'This Year (2026)',
        start: '2026-01-01',
        end: new Date().toISOString().split('T')[0]
      },
      {
        label: 'Last Month',
        start: (() => {
          const d = new Date();
          d.setMonth(d.getMonth() - 1);
          d.setDate(1);
          return d.toISOString().split('T')[0];
        })(),
        end: (() => {
          const d = new Date();
          d.setDate(0); // Last day of previous month
          return d.toISOString().split('T')[0];
        })()
      },
      {
        label: 'This Month',
        start: (() => {
          const d = new Date();
          d.setDate(1);
          return d.toISOString().split('T')[0];
        })(),
        end: new Date().toISOString().split('T')[0]
      }
    ],
    quarters: [
      {
        label: 'Q1 2026',
        start: '2026-01-01',
        end: '2026-03-31'
      },
      {
        label: 'Q2 2026',
        start: '2026-04-01',
        end: '2026-06-30'
      },
      {
        label: 'Q3 2026',
        start: '2026-07-01',
        end: '2026-09-30'
      },
      {
        label: 'Q4 2026',
        start: '2026-10-01',
        end: '2026-12-31'
      }
    ]
  };

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await inventoryService.getInventoryValueAsOfDate(startDate || null, endDate);
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate report');
      console.error('Failed to fetch inventory value report:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    if (!report) return;

    const reportData = report.comparison ? report.end_report : report;
    let csv = 'Inventory Value Report\n';
    
    if (report.comparison) {
      csv += `Period: ${formatDate(report.start_date)} to ${formatDate(report.end_date)}\n`;
      csv += `Start Value: ${formatCurrency(report.start_value)}\n`;
      csv += `End Value: ${formatCurrency(report.end_value)}\n`;
      csv += `Change: ${formatCurrency(report.value_change)} (${report.percent_change}%)\n\n`;
    } else {
      csv += `As of Date: ${formatDate(reportData.report_date)}\n`;
      csv += `Total Value: ${formatCurrency(reportData.total_value)}\n\n`;
    }

    if (viewMode === 'category' || viewMode === 'summary') {
      csv += 'By Category\n';
      csv += 'Category,Total Value\n';
      reportData.by_category.forEach(cat => {
        csv += `"${cat.category_name}",${cat.total_value}\n`;
      });
      csv += '\n';
    }

    if (viewMode === 'detail') {
      csv += 'Detailed Breakdown\n';
      csv += 'Item,Category,Lot ID,Quantity,Unit Price,Total Value,Date Received\n';
      reportData.by_item.forEach(item => {
        csv += `"${item.item_name}","${item.category_name}",${item.lot_id},${item.quantity},${item.unit_price},${item.total_value},${item.date_received}\n`;
      });
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const fileName = report.comparison 
      ? `inventory-value-${report.start_date}-to-${report.end_date}.csv`
      : `inventory-value-${reportData.report_date}.csv`;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Group items by category and item name for summary view
  const getItemSummary = () => {
    if (!report) return [];
    const reportData = report.comparison ? report.end_report : report;
    
    const itemMap = {};
    reportData.by_item.forEach(item => {
      const key = `${item.item_id}-${item.item_name}`;
      if (!itemMap[key]) {
        itemMap[key] = {
          item_id: item.item_id,
          item_name: item.item_name,
          category_name: item.category_name,
          total_quantity: 0,
          total_value: 0,
          lot_count: 0
        };
      }
      itemMap[key].total_quantity += item.quantity;
      itemMap[key].total_value += item.total_value;
      itemMap[key].lot_count += 1;
    });
    
    return Object.values(itemMap).sort((a, b) => 
      a.category_name.localeCompare(b.category_name) || a.item_name.localeCompare(b.item_name)
    );
  };

  const reportData = report ? (report.comparison ? report.end_report : report) : null;

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Inventory Value Report</h2>
          <p className="text-muted mb-0">
            {report && report.comparison 
              ? 'Period Comparison - For Financial Analysis'
              : 'For Tax & Financial Reporting'}
          </p>
        </div>
        {report && (
          <div className="btn-group no-print">
            <button className="btn btn-outline-secondary" onClick={handlePrint}>
              <i className="bi bi-printer me-2"></i>
              Print
            </button>
            <button className="btn btn-outline-secondary" onClick={handleExport}>
              <i className="bi bi-download me-2"></i>
              Export CSV
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      <div className="d-flex justify-content-end gap-2 mb-3 no-print">
        {startDate && (
          <button
            className="btn btn-outline-secondary"
            onClick={() => setStartDate('')}
          >
            Clear Start Date
          </button>
        )}
        <button
          className="btn btn-primary btn-lg"
          onClick={fetchReport}
          disabled={loading || !endDate}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Generating...
            </>
          ) : (
            <>
              <i className="bi bi-calculator me-2"></i>
              Generate Report
            </>
          )}
        </button>
      </div>

      <div className="row no-print">
        <div className="col-lg-9 mb-3">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title mb-3">Date Selection</h5>
              <div className="row align-items-end">
                <div className="col-md-5">
                  <label className="form-label">Start Date (Optional)</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || new Date().toISOString().split('T')[0]}
                  />
                  <small className="text-muted">
                    Leave blank for single date report
                  </small>
                </div>
                <div className="col-md-5">
                  <label className="form-label">End Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    min={startDate || undefined}
                  />
                  <small className="text-muted">
                    Calculate inventory value as of this date
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-lg-3 mb-3">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title mb-3">Quick Date Ranges</h5>
              <div className="row">
                <div className="col-md-6">
                  <div className="d-grid gap-1">
                    {quickDateRanges.periods.map((range, idx) => (
                      <button
                        key={idx}
                        className="btn btn-sm btn-outline-primary text-start"
                        onClick={() => setDateRange(range.start, range.end)}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-grid gap-1">
                    {quickDateRanges.quarters.map((range, idx) => (
                      <button
                        key={idx}
                        className="btn btn-sm btn-outline-primary text-start"
                        onClick={() => setDateRange(range.start, range.end)}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {report && !loading && (
        <>
          {/* Summary Card */}
          <div className="card mb-4">
            <div className="card-body">
              {report.comparison ? (
                // Comparison view
                <>
                  <div className="row text-center mb-4">
                    <div className="col-md-12">
                      <h5 className="text-muted mb-2">Period Comparison</h5>
                      <h6>{formatDate(report.start_date)} to {formatDate(report.end_date)}</h6>
                    </div>
                  </div>
                  <div className="row text-center">
                    <div className="col-md-3">
                      <h6 className="text-muted mb-2">Start Value</h6>
                      <h4>{formatCurrency(report.start_value)}</h4>
                    </div>
                    <div className="col-md-3">
                      <h6 className="text-muted mb-2">End Value</h6>
                      <h4>{formatCurrency(report.end_value)}</h4>
                    </div>
                    <div className="col-md-3">
                      <h6 className="text-muted mb-2">Change</h6>
                      <h3 className={report.value_change >= 0 ? 'text-success' : 'text-danger'}>
                        {report.value_change >= 0 ? '+' : ''}{formatCurrency(report.value_change)}
                      </h3>
                    </div>
                    <div className="col-md-3">
                      <h6 className="text-muted mb-2">% Change</h6>
                      <h3 className={report.percent_change >= 0 ? 'text-success' : 'text-danger'}>
                        {report.percent_change >= 0 ? '+' : ''}{report.percent_change.toFixed(2)}%
                      </h3>
                    </div>
                  </div>
                </>
              ) : (
                // Single date view
                <div className="row text-center">
                  <div className="col-md-4">
                    <h6 className="text-muted mb-2">Report Date</h6>
                    <h4>{formatDate(reportData.report_date)}</h4>
                  </div>
                  <div className="col-md-4">
                    <h6 className="text-muted mb-2">Total Inventory Value</h6>
                    <h2 className="text-success mb-0">{formatCurrency(reportData.total_value)}</h2>
                  </div>
                  <div className="col-md-4">
                    <h6 className="text-muted mb-2">Categories</h6>
                    <h4>{reportData.by_category.length}</h4>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* View Mode Selector */}
          <div className="card mb-4 no-print">
            <div className="card-body">
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn ${viewMode === 'summary' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('summary')}
                >
                  <i className="bi bi-list-ul me-2"></i>
                  Summary by Item
                </button>
                <button
                  type="button"
                  className={`btn ${viewMode === 'category' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('category')}
                >
                  <i className="bi bi-folder me-2"></i>
                  By Category
                </button>
                <button
                  type="button"
                  className={`btn ${viewMode === 'detail' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('detail')}
                >
                  <i className="bi bi-table me-2"></i>
                  Detailed by Lot
                </button>
              </div>
            </div>
          </div>

          {/* Category View */}
          {viewMode === 'category' && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Value by Category {report.comparison && `(as of ${formatDate(report.end_date)})`}</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th className="text-end">Total Value</th>
                        <th className="text-end">% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.by_category.map((cat, idx) => (
                        <tr key={idx}>
                          <td><strong>{cat.category_name}</strong></td>
                          <td className="text-end">{formatCurrency(cat.total_value)}</td>
                          <td className="text-end">
                            {((cat.total_value / reportData.total_value) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <td><strong>Total</strong></td>
                        <td className="text-end"><strong>{formatCurrency(reportData.total_value)}</strong></td>
                        <td className="text-end"><strong>100%</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Summary View by Item */}
          {viewMode === 'summary' && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Summary by Item {report.comparison && `(as of ${formatDate(report.end_date)})`}</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped table-sm">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Category</th>
                        <th className="text-end">Total Quantity</th>
                        <th className="text-end">Lots</th>
                        <th className="text-end">Total Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getItemSummary().map((item, idx) => (
                        <tr key={idx}>
                          <td><strong>{item.item_name}</strong></td>
                          <td><span className="badge bg-info">{item.category_name}</span></td>
                          <td className="text-end">{item.total_quantity}</td>
                          <td className="text-end">{item.lot_count}</td>
                          <td className="text-end">{formatCurrency(item.total_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan="4" className="text-end"><strong>Total</strong></td>
                        <td className="text-end"><strong>{formatCurrency(reportData.total_value)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Detail View by Lot */}
          {viewMode === 'detail' && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Detailed Breakdown by Lot {report.comparison && `(as of ${formatDate(report.end_date)})`}</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped table-sm">
                    <thead>
                      <tr>
                        <th>Lot #</th>
                        <th>Item</th>
                        <th>Category</th>
                        <th>Date Received</th>
                        <th className="text-end">Quantity</th>
                        <th className="text-end">Unit Price</th>
                        <th className="text-end">Total Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.by_item.map((item, idx) => (
                        <tr key={idx}>
                          <td>#{item.lot_id}</td>
                          <td>{item.item_name}</td>
                          <td><span className="badge bg-info">{item.category_name}</span></td>
                          <td>{formatDate(item.date_received)}</td>
                          <td className="text-end">{item.quantity}</td>
                          <td className="text-end">{formatCurrency(item.unit_price)}</td>
                          <td className="text-end">{formatCurrency(item.total_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan="6" className="text-end"><strong>Total</strong></td>
                        <td className="text-end"><strong>{formatCurrency(reportData.total_value)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .card {
            border: 1px solid #dee2e6 !important;
            box-shadow: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default InventoryValueReportPage;
